import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
  Chip,
} from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PhoneIcon from '@mui/icons-material/Phone';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import { useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { AppointmentTaker } from '../marketplace/types';

export const AppointmentDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [taker, setTaker] = useState<AppointmentTaker | null>(null);

  useEffect(() => {
    if (!slug) return;
    const load = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.APPOINTMENT_TAKER_BY_SLUG(slug));
        if (!response.ok) throw new Error('Not found');
        const data = await response.json();
        if (data.success && data.appointmentTaker) {
          setTaker(data.appointmentTaker);
        } else {
          throw new Error('Not found');
        }
      } catch (err) {
        setError('Consultant not found or unavailable.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const handleBookNow = () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/appointments/${slug}/book`)}`);
      return;
    }
    navigate(`/appointments/${slug}/book`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!taker) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Alert severity="error">{error || 'Consultant not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/appointments')}>
          View all consultants
        </Button>
      </Container>
    );
  }

  const feeDisplay = `${taker.currency} $${taker.fee.toFixed(2)}`;
  const showTax = taker.currency === 'CAD';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button onClick={() => navigate('/appointments')} sx={{ mb: 2, textTransform: 'none' }}>
        ← All consultants
      </Button>

      <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, border: '1px solid #e8eaed' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #3c3c3c 0%, #5c5c5c 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <EventAvailableIcon sx={{ fontSize: 56, color: '#fff' }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {taker.displayName}
            </Typography>

            {/* Availability flags */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {taker.allowCall && (
                <Chip
                  icon={<PhoneIcon sx={{ fontSize: 14 }} />}
                  label="Phone call"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {taker.allowWalkIn && (
                <Chip
                  icon={<DirectionsWalkIcon sx={{ fontSize: 14 }} />}
                  label="Walk-in"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {!taker.allowCall && !taker.allowWalkIn && (
                <Chip label="Not currently booking" size="small" color="default" />
              )}
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
              {feeDisplay}
              {showTax && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  + HST (13%)
                </Typography>
              )}
            </Typography>

            {taker.timeZone && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Timezone: {taker.timeZone}
              </Typography>
            )}

            {taker.officeAddress && taker.allowWalkIn && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Office: {taker.officeAddress.fullAddress}
              </Typography>
            )}

            <Button
              variant="contained"
              size="large"
              onClick={handleBookNow}
              disabled={!taker.allowCall && !taker.allowWalkIn}
              sx={{
                bgcolor: '#3c3c3c',
                color: '#fff',
                fontWeight: 600,
                px: 4,
                mt: 1,
                '&:hover': { bgcolor: '#222' },
                '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#999' },
              }}
            >
              {isAuthenticated ? 'Book appointment' : 'Sign in to book'}
            </Button>

            {!isAuthenticated && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Browse consultant details freely — sign in to pick a time and pay securely.
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};
